import React, { useEffect, useRef, useState } from "react";
import { Button, Input, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import toast, { Toaster } from "react-hot-toast";
import CostCenterService from "../../../../services/OrderingServices/CostCenterService";

export const DayPrintSummary = ({ responseData }) => {
  const socketRef = useRef(null);
  const [connectionStatus, setConnectionStatus] = useState("disconnected");
  const [userInfo, setUserInfo] = useState(null);
  const [webSocketUrl, setWebSocketUrl] = useState(null);
  const [isPrinting, setIsPrinting] = useState(false);
  const [costCenterData, setCostCenterData] = useState(null);

  // ✅ Modal states
  const [isMessageModal, setIsMessageModal] = useState(false);
  const [message, setMessage] = useState("");

  const fetchCostCenter = async () => {
    try {
      const response = await CostCenterService.getList();
      const actualData = Array.isArray(response)
        ? response
        : response?.data || [];

      const billPrinter = actualData.find(
        (item) => item.name?.trim().toLowerCase() === "bill printer"
      );
      setCostCenterData(billPrinter || null);
    } catch (error) {
      console.error("❌ Fetch cost center error:", error);
    }
  };

  useEffect(() => {
    fetchCostCenter();
  }, []);

  useEffect(() => {
    if (!costCenterData?.printerPort) return;

    const storedUserInfo = localStorage.getItem("user");
    if (storedUserInfo) {
      const parsedUserInfo = JSON.parse(storedUserInfo);
      setUserInfo(parsedUserInfo);
      const dynamicWebSocketUrl = `ws://localhost:${costCenterData.printerPort}`;
      setWebSocketUrl(dynamicWebSocketUrl);
    }
  }, [costCenterData]);

  useEffect(() => {
    if (!webSocketUrl) return;

    const connectWebSocket = () => {
      setConnectionStatus("connecting");
      socketRef.current = new WebSocket(webSocketUrl);

      socketRef.current.onopen = () => {
        setConnectionStatus("connected");
      };

      socketRef.current.onclose = () => {
        setConnectionStatus("disconnected");
      };

      socketRef.current.onerror = (error) => {
        console.error("WebSocket error:", error);
        setConnectionStatus("error");
        toast.error("Connection error. Please refresh or try again later.");
      };

      socketRef.current.onmessage = (event) => {
        try {
          const response = JSON.parse(event.data);
          if (response.status === "success") {
            toast.success(response.message || "Bill printed successfully");
          }
        } catch (error) {
          console.error("Error parsing WebSocket response:", error);
        } finally {
          setIsPrinting(false);
        }
      };
    };

    connectWebSocket();

    return () => {
      if (socketRef.current) {
        socketRef.current.close();
      }
    };
  }, [webSocketUrl]);

  // ✅ Called after user confirms modal
  const handleConfirmPrint = () => {
    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
      setIsPrinting(true);

      const paymentData =
        responseData?.map((item) => ({
          PaymentTypeName: item.paymentTypeName,
          TotalAmount: item.totalAmount,
        })) || [];

      const billData = {
        type: "PRINT_PAYMENT_SUMMARY",
        RestaurantName:
          userInfo?.organizationInfo?.name || "Unknown Restaurant",
        PrintedBy: userInfo?.user?.username || "Unknown User",
        Payments: paymentData,
        Message: message, // ✅ pass message entered in modal
      };

      socketRef.current.send(JSON.stringify(billData));
      setIsMessageModal(false); // close modal after sending
    } else {
      console.error("WebSocket not connected");
      setConnectionStatus("error");
      toast.error("WebSocket not connected. Please try again.");
    }
  };

  return (
    <div>
      <Toaster />
      <button
        className="btn btn-soft-success"
        title="Print"
        onClick={() => setIsMessageModal(true)} // ✅ Open modal first
        disabled={!webSocketUrl || isPrinting}
      >
        {isPrinting ? (
          "Printing..."
        ) : connectionStatus === "connecting" ? (
          "Connecting..."
        ) : (
          <i className="bx bx-printer"></i>
        )}
      </button>

      {/* ✅ Message Modal */}
      <Modal isOpen={isMessageModal} toggle={() => setIsMessageModal(false)}>
        <ModalHeader toggle={() => setIsMessageModal(false)}>
          Enter Message
        </ModalHeader>
        <ModalBody>
          <Input
            type="textarea"
            rows={6}
            className="form-control"
            placeholder="Enter message for day close"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          />
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setIsMessageModal(false)}>
            Cancel
          </Button>
          <Button color="success" onClick={handleConfirmPrint} disabled={!message.trim()}>
            Print
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};